import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../config/firebase';
import { CreateRequestInput, createAdminRequest } from '../../api/marketplace';
import RequestFields, { RequestFieldValues } from './RequestFields';
import { parseCodes } from './marketplaceFormat';
import './Marketplace.css';

interface Props {
  ticketId: string;
  userId: string;
  customerName: string;
  onClose: () => void;
}

// Opened from a support ticket by a marketplace admin to catch requests the
// classifier missed. The backend copies the customer from the ticket when no
// user ID is sent; the summary has to be written by hand and anonymised.
const CreateMarketplaceRequestDialog: React.FC<Props> = ({ ticketId, userId, customerName, onClose }) => {
  const navigate = useNavigate();
  const [values, setValues] = useState<RequestFieldValues>({
    serviceType: 'other',
    countries: '',
    language: 'en',
    urgency: 'normal',
    anonymisedSummary: '',
    userId,
    customerEmail: '',
    customerName,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const countries = parseCodes(values.countries, true);
    const summary = values.anonymisedSummary.trim();
    const language = values.language.trim().toLowerCase();
    if (!summary || countries.length === 0 || !language) {
      setError('Service, countries, language and an anonymised summary are required.');
      return;
    }
    const input: CreateRequestInput = {
      ticketId,
      serviceType: values.serviceType,
      countries,
      language,
      urgency: values.urgency,
      anonymisedSummary: summary,
    };
    if (values.userId.trim()) input.userId = values.userId.trim();
    if (values.customerEmail.trim()) input.customerEmail = values.customerEmail.trim();
    if (values.customerName.trim()) input.customerName = values.customerName.trim();

    const user = auth.currentUser;
    if (!user) {
      setError('You are signed out.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const created = await createAdminRequest(await user.getIdToken(), input);
      const id = created?.id || created?.request?.id;
      onClose();
      if (id) navigate(`/marketplace/admin/requests/${id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create the request.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="marketplace-dialog-backdrop" role="presentation" onClick={onClose}>
      <div
        className="marketplace-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Create marketplace request"
        onClick={e => e.stopPropagation()}
      >
        <form className="marketplace-form" onSubmit={handleSubmit}>
          <h3>Create marketplace request</h3>
          <p className="marketplace-hint">
            Partners only see the service, countries, language, urgency and the anonymised summary.
          </p>
          {error && <div className="status-error-banner" role="alert">{error}</div>}
          <RequestFields values={values} onChange={setValues} disabled={busy} />
          <div className="marketplace-actions">
            <button type="submit" className="download-button" disabled={busy}>Create request</button>
            <button type="button" className="logout-button" onClick={onClose} disabled={busy}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateMarketplaceRequestDialog;
